import type { Event } from '@/api/schemas'
import { formatLocalTime, groupByLocalHour } from '@/lib/format'
import { cn } from '@/lib/utils'
import { t } from '@/lib/i18n'

interface Props {
  events: Event[]
  onSelectEvent?: (eventId: number) => void
}

/** Camera-scoped events, grouped by local hour. Keeps the richer row (description
 *  + alert border) that the camera page needs — the site-level EventsByHour only
 *  renders trigger/camera and is not reusable here. */
export function CameraEventsByHour({ events, onSelectEvent }: Props) {
  const groups = groupByLocalHour(events, (e) => e.timestamp)

  return (
    <div>
      <h2 className="text-sm font-semibold text-muted-foreground uppercase tracking-wider mb-3">
        {t('Recent events')}
      </h2>
      <div className="bg-card rounded-xl border border-border divide-y divide-border-subtle">
        {groups.length === 0 ? (
          <div className="px-4 py-8 text-center text-sm text-subtle-foreground">
            {t('No events for this camera')}
          </div>
        ) : (
          groups.map((group) => (
            <details key={group.hour} className="group">
              <summary className="flex items-center gap-3 px-4 py-2.5 cursor-pointer hover:bg-background list-none">
                <span className="text-xs text-subtle-foreground group-open:text-foreground-muted transition-colors">
                  ▸
                </span>
                <span className="text-xs font-medium text-foreground-soft tabular-nums">{group.hour}</span>
                <span className="text-xs text-subtle-foreground">
                  {group.items.length === 1 ? t('1 capture') : t('{n} captures', { n: group.items.length })}
                </span>
                {group.items.some((e) => e.alert) ? (
                  <span className="w-1.5 h-1.5 rounded-full bg-critical flex-shrink-0" />
                ) : null}
              </summary>
              <div className="divide-y divide-background">
                {group.items.map((event) => (
                  <EventRow key={event.id} event={event} onSelect={onSelectEvent} />
                ))}
              </div>
            </details>
          ))
        )}
      </div>
    </div>
  )
}

function EventRow({ event, onSelect }: { event: Event; onSelect?: (id: number) => void }) {
  const borderClass = event.alert
    ? event.alert.level === 'critical'
      ? 'border-l-2 border-l-critical'
      : 'border-l-2 border-l-attention'
    : ''
  const className = cn(
    'flex items-center gap-3 px-4 pl-10 py-2 hover:bg-background transition-colors w-full text-left',
    borderClass,
  )
  const content = (
    <>
      <span className="text-xs text-subtle-foreground w-12 flex-shrink-0 tabular-nums">
        {formatLocalTime(event.timestamp)}
      </span>
      <code className="text-xs px-1.5 py-0.5 rounded bg-muted text-muted-foreground flex-shrink-0 font-mono">
        {event.trigger}
      </code>
      <span className="text-xs text-foreground truncate flex-1">
        {event.description || event.status}
      </span>
      {event.relevance ? (
        <span
          className={cn(
            'text-xs flex-shrink-0',
            event.relevance === 'high' && 'text-critical',
            event.relevance === 'medium' && 'text-attention',
            event.relevance !== 'high' && event.relevance !== 'medium' && 'text-subtle-foreground',
          )}
        >
          {event.relevance}
        </span>
      ) : null}
    </>
  )
  if (onSelect) {
    return (
      <button
        type="button"
        className={className}
        data-event-id={event.id}
        onClick={() => onSelect(event.id)}
      >
        {content}
      </button>
    )
  }
  return (
    <div className={className} data-event-id={event.id}>
      {content}
    </div>
  )
}
